// Filling Payables (gap #9): the vendors (vendors.list, create, update), a bill or a vendor's credit
// note entered straight to accounts (vendorBills.create, createCredit), and an approved credit used on
// one of that vendor's bills (vendorBills.applyCredit). Nothing posts until a bill is approved in
// "To approve"; every refusal is the server's.

import { useState } from 'react';
import { ClosedMonthHint, Empty, Loading, Money, Panel, RowActions, useAct, useQuery } from './ui.tsx';
import { dayText, parseMoney, todayIn } from './model.ts';
import { useSaveKey } from '../shared/SaveButton.tsx';
import type { VendorBill } from './types.ts';
import '../shared/office-lists.css';

export interface Vendor {
  id: string;
  name: string;
  email: string | null;
  termsDays: number;
  defaultAccountId: string | null;
  active: boolean;
}
interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  active: boolean;
}
interface Zone {
  components: { code: string }[];
}

/** Who enters bills and vendors (ap.manage): the owner, the manager and the bookkeeper. */
export const AP_PERMS = ['ap.manage'];

const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
const accountLabel = (a: Account) => `${a.code} ${a.name}`;
/** A bill goes to what was bought: an expense, or an asset that isn't money (stock, a tool, a van). */
const billable = (a: Account) =>
  a.type === 'expense' || (a.type === 'asset' && !/bank|cash|undeposited|receivable/i.test(a.name));

// ── vendors ──

function VendorForm({
  vendor,
  accounts,
  busy,
  onSave,
  onCancel,
}: {
  vendor: Vendor | null;
  accounts: Account[];
  busy: boolean;
  onSave: (v: {
    name: string;
    email: string;
    termsDays: number;
    defaultAccountId: string;
    active: boolean;
  }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(vendor?.name ?? '');
  const [email, setEmail] = useState(vendor?.email ?? '');
  const [terms, setTerms] = useState(String(vendor?.termsDays ?? 30));
  const [account, setAccount] = useState(vendor?.defaultAccountId ?? '');
  const termsOk = /^\d{1,3}$/.test(terms.trim()) && Number(terms) <= 365;
  const ok = !!name.trim() && termsOk;
  return (
    <form
      className="fgrid inline-form"
      aria-label={vendor ? `Change ${vendor.name}` : 'A new vendor'}
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !busy)
          onSave({
            name: name.trim(),
            email: email.trim(),
            termsDays: Number(terms),
            defaultAccountId: account,
            active: true,
          });
      }}
    >
      <label className="wide">
        <span className="flabel">Name</span>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <label>
        <span className="flabel">Email for purchase orders</span>
        <input
          className="field"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Optional"
        />
      </label>
      <label>
        <span className="flabel">Pay within (days)</span>
        <input
          className="field"
          inputMode="numeric"
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
        />
      </label>
      <label className="wide">
        <span className="flabel">Usual account</span>
        <select className="field" value={account} onChange={(e) => setAccount(e.target.value)}>
          <option value="">None</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {accountLabel(a)}
            </option>
          ))}
        </select>
      </label>
      <div className="row-actions">
        <button className="btn primary" type="submit" disabled={!ok || busy}>
          {vendor ? 'Save the vendor' : 'Add the vendor'}
        </button>
        <button className="btn quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function VendorsPanel({ toast, onChanged }: { toast: (m: string) => void; onChanged: () => void }) {
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const act = useAct(toast, async () => {
    setOpen(null);
    await vendors.reload();
    onChanged();
  });
  const expense = (accounts.data?.accounts ?? []).filter(billable);
  return (
    <Panel
      title="Vendors"
      aside={
        open !== 'new' ? (
          <button className="btn" onClick={() => setOpen('new')}>
            Add a vendor
          </button>
        ) : undefined
      }
    >
      {open === 'new' && (
        <VendorForm
          vendor={null}
          accounts={expense}
          busy={act.busy}
          onCancel={() => setOpen(null)}
          onSave={(v) =>
            void act.run(
              'vendors.create',
              {
                name: v.name,
                ...(v.email ? { email: v.email } : {}),
                termsDays: v.termsDays,
                ...(v.defaultAccountId ? { defaultAccountId: v.defaultAccountId } : {}),
              },
              `${v.name} is a vendor.`,
            )
          }
        />
      )}
      {!vendors.data ? (
        <Loading error={vendors.error} what="the vendors" retry={() => void vendors.reload()} />
      ) : vendors.data.vendors.length === 0 ? (
        <Empty>No vendors yet. Add the suppliers and payees whose bills come in.</Empty>
      ) : (
        <ul className="rows vendor-rows">
          {vendors.data.vendors.map((v) => (
            <li key={v.id}>
              {open === v.id ? (
                <VendorForm
                  vendor={v}
                  accounts={expense}
                  busy={act.busy}
                  onCancel={() => setOpen(null)}
                  onSave={(n) =>
                    void act.run(
                      'vendors.update',
                      {
                        id: v.id,
                        name: n.name,
                        email: n.email || null,
                        termsDays: n.termsDays,
                        defaultAccountId: n.defaultAccountId || null,
                      },
                      `${n.name} is saved.`,
                    )
                  }
                />
              ) : (
                <div className="line-top">
                  <div>
                    <strong>{v.name}</strong>
                    <span className="muted small">
                      Pay within {v.termsDays} days{v.email ? ` · ${v.email}` : ''}
                      {!v.active ? ' · switched off' : ''}
                    </span>
                  </div>
                  <RowActions>
                    <button className="btn quiet" onClick={() => setOpen(v.id)}>
                      Change
                    </button>
                    <button
                      className="btn quiet"
                      disabled={act.busy}
                      onClick={() =>
                        void act.run(
                          'vendors.update',
                          { id: v.id, active: !v.active },
                          v.active ? `${v.name} is switched off.` : `${v.name} is back on.`,
                        )
                      }
                    >
                      {v.active ? 'Switch off' : 'Switch on'}
                    </button>
                  </RowActions>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── a bill or a credit note, straight to accounts ──

interface Line {
  accountId: string;
  description: string;
  amount: string;
}

export function EnterBill({
  toast,
  timeZone,
  onChanged,
}: {
  toast: (m: string) => void;
  timeZone?: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', open ? {} : null);
  const accounts = useQuery<{ accounts: Account[] }>(
    'accounts.list',
    open ? { includeInactive: false } : null,
  );
  const zones = useQuery<{ zones: Zone[] }>('tax.listZones', open ? {} : null);
  const [kind, setKind] = useState<'bill' | 'credit'>('bill');
  const [vendorId, setVendorId] = useState('');
  const [ref, setRef] = useState('');
  const [billDate, setBillDate] = useState(todayIn(timeZone));
  const [dueDate, setDueDate] = useState('');
  const [creditFor, setCreditFor] = useState('');
  const [lines, setLines] = useState<Line[]>([{ accountId: '', description: '', amount: '' }]);
  const [tax, setTax] = useState('');
  const bills = useQuery<{ bills: VendorBill[] }>(
    'vendorBills.list',
    open && kind === 'credit' && vendorId ? { vendorId } : null,
  );
  const reset = () => {
    setOpen(false);
    setRef('');
    setDueDate('');
    setCreditFor('');
    setLines([{ accountId: '', description: '', amount: '' }]);
    setTax('');
  };
  const act = useAct(toast, async () => {
    reset();
    onChanged();
  });
  const save = useSaveKey(JSON.stringify([kind, vendorId, ref, billDate, dueDate, creditFor, lines, tax]));
  const live = (vendors.data?.vendors ?? []).filter((v) => v.active);
  const vendor = live.find((v) => v.id === vendorId);
  const accountList = (accounts.data?.accounts ?? []).filter(billable);
  // Recoverable tax on a bill (input tax credits) is GST or HST; BC PST is part of the line amounts.
  const taxCode =
    (zones.data?.zones ?? [])
      .flatMap((z) => z.components.map((c) => c.code))
      .find((c) => c === 'HST' || c === 'GST') ?? 'GST';
  const cents = lines.map((l) => parseMoney(l.amount));
  const taxCents = tax.trim() ? parseMoney(tax) : 0;
  const linesOk = lines.every((l, i) => l.accountId && l.description.trim() && (cents[i] ?? 0) > 0);
  const ok = !!vendor && !!ref.trim() && !!billDate && linesOk && taxCents !== null && taxCents >= 0;
  const total = cents.reduce<number>((t, c) => t + (c ?? 0), 0) + (taxCents ?? 0);
  const setLine = (i: number, patch: Partial<Line>) =>
    setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const forBills = (bills.data?.bills ?? []).filter(
    (b) => b.kind === 'bill' && b.heldCents + b.owingCents > 0,
  );

  if (!open)
    return (
      <div className="row-actions">
        <button className="btn primary" onClick={() => setOpen(true)}>
          Enter a bill
        </button>
        <button
          className="btn"
          onClick={() => {
            setKind('credit');
            setOpen(true);
          }}
        >
          Enter a vendor credit
        </button>
      </div>
    );
  return (
    <Panel title={kind === 'bill' ? 'A new bill' : 'A vendor’s credit note'}>
      {!vendors.data || !accounts.data ? (
        <Loading error={vendors.error ?? accounts.error} what="the vendors and accounts" />
      ) : live.length === 0 ? (
        <Empty>Add the vendor first (Vendors, below), then enter their bill.</Empty>
      ) : (
        <form
          className="fgrid"
          aria-label={kind === 'bill' ? 'A new bill' : 'A vendor’s credit note'}
          onSubmit={(e) => {
            e.preventDefault();
            if (!ok || act.busy) return;
            const input = {
              vendorId,
              vendorRef: ref.trim(),
              billDate,
              ...(dueDate ? { dueDate } : {}),
              lines: lines.map((l, i) => ({
                accountId: l.accountId,
                description: l.description.trim(),
                amountCents: cents[i]!,
              })),
              taxes: taxCents ? [{ code: taxCode, taxCents }] : [],
            };
            void act.run<{ number: string }>(
              kind === 'bill' ? 'vendorBills.create' : 'vendorBills.createCredit',
              kind === 'credit' && creditFor ? { ...input, creditForId: creditFor } : input,
              kind === 'bill'
                ? 'The bill is waiting in To approve.'
                : 'The credit note is waiting in To approve.',
              { idempotencyKey: save.key },
            );
          }}
        >
          <div className="seg full" role="group" aria-label="What came in">
            <button
              type="button"
              className={`btn quiet${kind === 'bill' ? ' on' : ''}`}
              aria-pressed={kind === 'bill'}
              onClick={() => setKind('bill')}
            >
              A bill
            </button>
            <button
              type="button"
              className={`btn quiet${kind === 'credit' ? ' on' : ''}`}
              aria-pressed={kind === 'credit'}
              onClick={() => setKind('credit')}
            >
              A credit note
            </button>
          </div>
          <label className="wide">
            <span className="flabel">Vendor</span>
            <select
              className="field"
              value={vendorId}
              onChange={(e) => {
                setVendorId(e.target.value);
                const v = live.find((x) => x.id === e.target.value);
                if (v?.defaultAccountId && lines.length === 1 && !lines[0]!.accountId)
                  setLine(0, { accountId: v.defaultAccountId });
              }}
            >
              <option value="">Pick a vendor…</option>
              {live.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="flabel">
              {kind === 'bill' ? 'Their bill number' : 'Their credit note number'}
            </span>
            <input className="field" value={ref} onChange={(e) => setRef(e.target.value)} />
          </label>
          <label>
            <span className="flabel">{kind === 'bill' ? 'Billed on' : 'Dated'}</span>
            <input
              className="field"
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
            />
            <ClosedMonthHint date={billDate} />
          </label>
          {kind === 'bill' && (
            <label>
              <span className="flabel">Due</span>
              <span className="muted small block">
                Leave it empty for the vendor’s terms{vendor ? ` (${vendor.termsDays} days)` : ''}.
              </span>
              <input
                className="field"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>
          )}
          {kind === 'credit' && (
            <p className="wide muted small">
              For parts sent back on a return, enter their credit from Returns awaiting credit instead: it
              clears the return.
            </p>
          )}
          {kind === 'credit' && vendorId && (
            <label className="wide">
              <span className="flabel">Against a bill</span>
              <select className="field" value={creditFor} onChange={(e) => setCreditFor(e.target.value)}>
                <option value="">None: a credit to use later</option>
                {forBills.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.number} · {b.vendorRef} · {cad(b.totalCents)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {lines.map((l, i) => (
            <div key={i} className="move-line bill-line">
              <label>
                <span className="flabel">Account</span>
                <select
                  className="field"
                  value={l.accountId}
                  onChange={(e) => setLine(i, { accountId: e.target.value })}
                >
                  <option value="">Pick an account…</option>
                  {accountList.map((a) => (
                    <option key={a.id} value={a.id}>
                      {accountLabel(a)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="flabel">What it’s for</span>
                <input
                  className="field"
                  value={l.description}
                  onChange={(e) => setLine(i, { description: e.target.value })}
                />
              </label>
              <label>
                <span className="flabel">Amount</span>
                <input
                  className="field qty-in"
                  inputMode="decimal"
                  value={l.amount}
                  onChange={(e) => setLine(i, { amount: e.target.value })}
                />
              </label>
              {lines.length > 1 && (
                <button
                  type="button"
                  className="btn quiet"
                  aria-label={`Remove line ${i + 1}`}
                  onClick={() => setLines(lines.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <label>
            <span className="flabel">{taxCode} on it (recoverable)</span>
            <input
              className="field"
              inputMode="decimal"
              value={tax}
              onChange={(e) => setTax(e.target.value)}
              placeholder="0.00"
            />
          </label>
          <p className="wide muted small">
            Total {cad(total)}. Nothing posts until it’s approved in To approve.
          </p>
          <div className="row-actions">
            <button
              type="button"
              className="btn"
              onClick={() =>
                setLines([
                  ...lines,
                  { accountId: vendor?.defaultAccountId ?? '', description: '', amount: '' },
                ])
              }
            >
              Another line
            </button>
            <button className="btn primary" type="submit" disabled={!ok || act.busy}>
              {act.busy ? 'Saving…' : kind === 'bill' ? 'Save the bill' : 'Save the credit note'}
            </button>
            <button className="btn quiet" type="button" onClick={reset} disabled={act.busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

// ── an approved credit, used on one of that vendor's bills ──

export function VendorCredits({
  toast,
  version,
  onChanged,
}: {
  toast: (m: string) => void;
  version: number;
  onChanged: () => void;
}) {
  const approved = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'approved' }, [version]);
  const [open, setOpen] = useState<string | null>(null);
  const [billId, setBillId] = useState('');
  const [amount, setAmount] = useState('');
  const act = useAct(toast, async () => {
    setOpen(null);
    await approved.reload();
    onChanged();
  });
  const all = approved.data?.bills ?? [];
  const credits = all.filter((b) => b.kind === 'credit' && b.approvedCents - b.settledCents > 0);
  if (!approved.data || credits.length === 0) return null;
  return (
    <Panel title="Vendor credits to use" aside={<span className="chip">{credits.length}</span>}>
      <ul className="cards">
        {credits.map((c) => {
          const left = c.approvedCents - c.settledCents;
          const bills = all.filter((b) => b.kind === 'bill' && b.vendorId === c.vendorId && b.owingCents > 0);
          const target = bills.find((b) => b.id === billId);
          const cents = parseMoney(amount);
          const max = Math.min(left, target?.owingCents ?? left);
          const ok = !!target && cents !== null && cents > 0 && cents <= max;
          return (
            <li key={c.id} className="stone line-card">
              <div className="line-top">
                <div>
                  <strong>
                    {c.number} · {c.vendorName}
                  </strong>
                  <span className="muted small">
                    {c.vendorRef} · dated {dayText(c.billDate)}
                  </span>
                </div>
                <Money cents={left} className="amount" />
              </div>
              {open === c.id ? (
                bills.length === 0 ? (
                  <p className="muted small">Nothing from {c.vendorName} is owing to use it on.</p>
                ) : (
                  <div className="fgrid inline-form">
                    <label className="wide">
                      <span className="flabel">Use it on</span>
                      <select
                        className="field"
                        value={billId}
                        onChange={(e) => {
                          setBillId(e.target.value);
                          const b = bills.find((x) => x.id === e.target.value);
                          if (b) setAmount((Math.min(left, b.owingCents) / 100).toFixed(2));
                        }}
                      >
                        <option value="">Pick a bill…</option>
                        {bills.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.number} · {b.vendorRef} · {cad(b.owingCents)} owing
                          </option>
                        ))}
                      </select>
                    </label>
                    {target && (
                      <label>
                        <span className="flabel">Amount (up to {cad(max)})</span>
                        <input
                          className="field qty-in"
                          inputMode="decimal"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />
                      </label>
                    )}
                    <div className="row-actions">
                      <button
                        className="btn primary"
                        disabled={!ok || act.busy}
                        onClick={() =>
                          void act.run(
                            'vendorBills.applyCredit',
                            { creditId: c.id, billId, amountCents: cents },
                            `${cad(cents ?? 0)} of ${c.number} went on ${target?.number}.`,
                          )
                        }
                      >
                        Use {cad(ok ? cents! : 0)}
                      </button>
                      <button className="btn quiet" onClick={() => setOpen(null)} disabled={act.busy}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div className="row-actions">
                  <button
                    className="btn"
                    onClick={() => {
                      setOpen(c.id);
                      setBillId('');
                      setAmount('');
                    }}
                  >
                    Use on a bill…
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
